import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, CheckCircle, Loader2, Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InterviewSelectPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr-pipeline/interview-slots/${token}`);
      if (!res.ok) throw new Error('Link not found');
      const result = await res.json();
      setData(result);
      
      if (result.status === 'confirmed' && result.selected_slot) {
        setConfirmed(true);
        setSelectedSlot(result.selected_slot);
      }
    } catch (err) {
      setError('not_found');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setConfirming(true);
    try {
      const formData = new FormData();
      formData.append('date', selectedSlot.date);
      formData.append('time', selectedSlot.time);

      const res = await fetch(`${API_URL}/api/hr-pipeline/confirm-slot/${token}`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to confirm');
      
      setConfirmed(true);
      toast.success('Interview confirmed!');
    } catch (err) {
      toast.error('Failed to confirm slot');
    } finally {
      setConfirming(false);
    }
  };

  // Group slots by date
  const groupSlotsByDate = (slots) => {
    const grouped = {};
    slots?.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Link Not Found</h1>
            <p className="text-slate-600">This interview link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Interview Confirmed!</h1>
            <div className="bg-green-50 rounded-lg p-4 mt-4 text-left">
              <p className="text-sm text-slate-600 mb-2">Your interview is scheduled for:</p>
              <p className="font-semibold text-slate-800">
                {new Date(selectedSlot?.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <p className="text-slate-600">at {selectedSlot?.time}</p>
              <p className="text-sm text-slate-500 mt-2">
                Duration: {data.duration_minutes} minutes
              </p>
            </div>
            <p className="text-sm text-slate-500 mt-4">
              A calendar invite has been sent to your email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const groupedSlots = groupSlotsByDate(data.slots);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-lg mx-auto px-4 py-6">
          <p className="text-blue-100 text-sm mb-1">Interview Invitation</p>
          <h1 className="text-xl font-bold">{data.job_title}</h1>
          {data.company_name && (
            <p className="text-blue-100 flex items-center gap-1 mt-1">
              <Building2 className="w-4 h-4" />
              {data.company_name}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-600 text-sm">
              Hi <span className="font-medium">{data.candidate_name}</span>,
            </p>
            <p className="text-slate-600 text-sm mt-2">
              Great news! The hiring team would like to schedule an interview with you. 
              Please select your preferred time slot below.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {Object.entries(groupedSlots).map(([date, slots]) => (
            <Card key={date}>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800 flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((slot, i) => {
                    const isSelected = selectedSlot?.date === slot.date && selectedSlot?.time === slot.time;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedSlot({ date: slot.date, time: slot.time })}
                        className={`
                          py-2 px-3 rounded-lg text-sm font-medium transition-all border
                          ${isSelected 
                            ? 'bg-blue-600 text-white border-blue-600' 
                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                          }
                        `}
                      >
                        <Clock className="w-3 h-3 inline mr-1" />
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Interview Duration Info */}
        <div className="bg-slate-100 rounded-lg p-3 text-center">
          <p className="text-sm text-slate-600">
            <Clock className="w-4 h-4 inline mr-1" />
            Interview Duration: <span className="font-medium">{data.duration_minutes} minutes</span>
          </p>
        </div>

        {/* Confirm Button */}
        <Button 
          onClick={handleConfirm} 
          disabled={!selectedSlot || confirming}
          className="w-full"
          size="lg"
        >
          {confirming ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <CheckCircle className="w-4 h-4 mr-2" />
          )}
          Confirm Interview
        </Button>

        {selectedSlot && (
          <p className="text-center text-sm text-slate-600">
            Selected: {new Date(selectedSlot.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {selectedSlot.time}
          </p>
        )}
      </div>
    </div>
  );
}
